import {
    Avatar,
    Colors,
    FOCUS_RING,
    Icon,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import { calculatedLevelGap } from './calculatedLevelGap';
import { ScoreArc } from './ScoreArc';
import { scoreOutOfLevels, shareOfScale } from './scoreOutOfLevels';

import type { PerformanceScore } from '@/types/performance-profile';

import { useGetPerformanceScoreLevels } from '@/api/competency-services/performance-score-level';
import { useGetProficiencyLevels } from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';
import { formatDecimal } from '@/utils/numberFormat';
import { getInitials } from '@/utils/user';

interface PerformanceScoreDialProps {
    score: PerformanceScore;
    /**
     * The period the score covers, as the competency filters name it; `null` reads every period.
     * The same words on both tabs: the score is the page's, and the filter that narrows it is
     * visible on one tab only.
     */
    periodLabel?: string | null;
    /** Prefix for the parts a test reads; the wrapper stays anonymous so the host keeps its own. */
    dataTestId?: string;
}

/**
 * The employee's performance score as a dial: the arc, their picture in the middle, and the score
 * on the company's own level scale ("3,4 / 5"), nothing else. Shared by the "Évaluation" and
 * "Croissance" tabs, which wrap it in their own surface and title.
 *
 * The denominator is read, never written: it is the number of proficiency levels the company has
 * configured -- the same list the server scores against (`/proficiencylevel`). The neighbouring
 * `/PerformanceScoreLevel` resource sounds like the right one and is not; both hold five rows in
 * this database, so picking the wrong one would look correct here and be wrong at a client.
 */
export const PerformanceScoreDial = ({
    score,
    periodLabel = null,
    dataTestId = 'performance-score-dial',
}: PerformanceScoreDialProps) => {
    const { t, i18n } = useAmelioTranslations();
    const {
        data: proficiencyLevels,
        isLoading: areLevelsLoading,
        isError: areLevelsInError,
    } = useGetProficiencyLevels();
    const totalLevels = proficiencyLevels?.length ?? 0;
    // The other list of five: the company's performance scale, which turns a score into a level.
    const { data: scoreLevels = [] } = useGetPerformanceScoreLevels();
    const gapLevel = calculatedLevelGap({ score, levels: scoreLevels });

    const employeeName = score.employeeName ?? '';
    const initials = getInitials(employeeName);

    // No score is not a score of 0: a real 0 is the lowest level, and reads as "0 / 5" rightly.
    const isEvaluated = score.score != null;
    const value = scoreOutOfLevels({ score: score.score, totalLevels });

    // The single place this screen decides whether the level was applied by hand. The legend under
    // the dial and the reason inside the tooltip both read it, so the two can never disagree.
    const isManuallyApplied = score.levelSource === 'ManagerChosen';
    const scopePeriod =
        periodLabel ?? t('performanceProfile.growth.score.scope.mostRecentAssessments');

    // What the dial states is the level the score reaches on the company's own scale. The figure
    // behind it is not written: a reader asked for the level, and "2,7 / 4" is the arithmetic, not
    // the answer. It comes back only when there is no level to name.
    //
    // Not knowing the scale is not knowing the employee: while the levels load or fail, the dial
    // says so, and "not evaluated yet" stays a statement about the employee alone.
    const readScore = (): string => {
        if (score.level) {
            return getLocalizedText(score.level.label);
        }

        if (areLevelsLoading) {
            return t('performanceProfile.loading');
        }
        if (areLevelsInError) {
            return t('performanceProfile.growth.score.levelsError');
        }
        if (!isEvaluated) {
            // Nothing found through a period lens is not "nothing at all": the trend beside this
            // tile reads every cycle, so a tile that stated the general case would contradict it.
            return periodLabel
                ? t('performanceProfile.growth.score.notEvaluatedInPeriod', { period: periodLabel })
                : t('performanceProfile.growth.score.notEvaluated');
        }
        if (value === null) {
            return t('performanceProfile.growth.score.noLevels');
        }
        return t('performanceProfile.growth.score.outOfLevels', {
            value: formatDecimal(value, i18n.language),
            total: totalLevels,
        });
    };
    const reading = readScore();

    return (
        <div className={'flex flex-col items-center gap-4'}>
            {/* The dial owns its own geometry, the picture included: `fill` lets the avatar take
                the diameter the ring derives, instead of a size from the library's scale that
                would drift from it. */}
            <ScoreArc percentage={shareOfScale({ value, totalLevels })}>
                <Avatar
                    src={score.avatarUrl || undefined}
                    initials={initials || undefined}
                    alt={t('performanceProfile.growth.score.avatarAlt', {
                        name: employeeName,
                    })}
                    fill
                    bgColor={'gradient'}
                />
            </ScoreArc>

            <div className={'flex items-center gap-1.5'}>
                {/* A step below the card's own title: the level is what the dial reads out, not a
                    second heading competing with it. */}
                <p
                    className={'text-navy text-body-lg text-center font-semibold'}
                    data-testid={`${dataTestId}-value`}
                    aria-busy={areLevelsLoading}
                >
                    {reading}
                </p>

                <TooltipProvider>
                    <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                            {/* The whole disc is the target, not just the glyph: 24px is the
                                smallest a pointer should have to find. */}
                            <button
                                type={'button'}
                                className={`${FOCUS_RING} flex size-6 shrink-0 items-center justify-center rounded-full`}
                                aria-label={t('performanceProfile.growth.score.howItIsRead.label')}
                                data-testid={`${dataTestId}-how-it-is-read`}
                            >
                                <Icon
                                    name={'essential-info-circle'}
                                    size={16}
                                    color={Colors.BASE_400}
                                    ariaHidden
                                />
                            </button>
                        </TooltipTrigger>

                        <TooltipContent
                            className={'max-w-xs'}
                            side={'top'}
                        >
                            <ul className={'flex flex-col gap-1'}>
                                <li>
                                    {t('performanceProfile.growth.score.howItIsRead.submittedOnly')}
                                </li>
                                <li>
                                    {t(
                                        'performanceProfile.growth.score.howItIsRead.oneVoicePerEvaluator',
                                    )}
                                </li>
                                <li>
                                    {t(
                                        'performanceProfile.growth.score.howItIsRead.keepsLastKnown',
                                    )}
                                </li>
                                {/* Only when the level on screen is a manager's own: the three
                                    sentences above describe an arithmetic that did not decide it,
                                    and a reader told the level was applied by hand is owed the
                                    reason. Said of a calculated level, it would be untrue. */}
                                {isManuallyApplied && (
                                    <li>
                                        {t(
                                            'performanceProfile.growth.score.howItIsRead.managerChose',
                                        )}
                                    </li>
                                )}
                            </ul>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            {score.levelSource && (
                // Two figures that can disagree have to say which one is on screen: a manager may
                // have applied the level by hand rather than let the scale resolve it, and the
                // period the score covers is only visible on the tab that carries the filter.
                // Both are drawn from the same evaluations, so unfiltered they name that one
                // reach: a manually applied level is the latest evaluation's.
                <p
                    className={'text-growth-slate text-center text-[12px] leading-[16px]'}
                    data-testid={`${dataTestId}-scope`}
                >
                    {isManuallyApplied
                        ? t('performanceProfile.growth.score.scope.managerChosen', {
                              period: scopePeriod,
                          })
                        : t('performanceProfile.growth.score.scope.calculated', {
                              period: scopePeriod,
                          })}
                </p>
            )}

            {gapLevel && (
                // The arc keeps being filled by the score while the word above is the manager's,
                // so the scale's own answer is named rather than left to be inferred from a ring.
                <p
                    className={'text-growth-slate flex items-center gap-1 text-center text-xs'}
                    data-testid={`${dataTestId}-calculated-gap`}
                >
                    <Icon
                        name={'essential-warning-2'}
                        size={14}
                        color={Colors.WARNING_900}
                        ariaHidden
                    />
                    {t('performanceProfile.growth.score.calculatedGap', {
                        level: getLocalizedText(gapLevel.label),
                    })}
                </p>
            )}
        </div>
    );
};
